import { Emit } from "@/api/apiHelper";
import WalletTransaction, {
  WalletTransactionType,
} from "@/models/wallet/WalletTransaction";
import camelcaseKeys from "camelcase-keys";
import Vue from "vue";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

/**
 * 👾 Module which handles general purpose functionality for the application
 */
@Module
export default class WalletModule extends VuexModule {
  // 📦 The user's deposit address on OB
  public depositWalletAddress: string | null = null;

  // 📦 The hot wallet balance for the current user
  public hotWalletBalance: number | null = null;

  // 📦 The cold wallet balance for the current user
  public coldWalletBalance: number | null = null;

  // 📦 The current price of OBT in USD
  public currentOBTPrice: number | null = null;

  // 📦 The current OBT holding rank of the user
  public OBTHoldingRank: string | null = null;

  // 📦 The cold wallet balance for the current user
  private estimatedFees: any = null;

  // 📅 The last time the wallet balance has been fetched
  private lastUpdateTime: Date | null = null;

  // 📦 The currently available OBT balance for the user
  public get availableWalletBalance(): number {
    return this.hotWalletBalance ? this.hotWalletBalance : 0;
  }

  // 📦 All the transactions for the current user's OBT wallet
  public walletTransactions: WalletTransaction[] = [];

  // 📦 All the deposit transactions for the current user's OBT wallet
  public get depositWalletTransactions(): WalletTransaction[] {
    return this.walletTransactions.filter(
      (transaction: WalletTransaction) =>
        transaction.type === WalletTransactionType.Deposit ||
        transaction.type === WalletTransactionType.ReferralReward
    );
  }

  // 📦 The combined amount of the referral rewards
  public get referralRewardsAmount(): number {
    return this.walletTransactions
      .filter(
        (transaction: WalletTransaction) =>
          transaction.type === WalletTransactionType.ReferralReward
      )
      .map((transaction: WalletTransaction) => transaction.amount)
      .reduce((a: any, b: any) => Number(a) + Number(b), 0);
  }

  // 📦 All the withdraw transactions for the current user's OBT wallet
  public get withdrawWalletTransactions(): WalletTransaction[] {
    return this.walletTransactions.filter(
      (transaction: WalletTransaction) =>
        transaction.type === WalletTransactionType.Withdraw ||
        transaction.type === WalletTransactionType.Subscription ||
        transaction.type === WalletTransactionType.FeePerTrade
    );
  }

  // 📦 All the subscription plan transactions for the current user's OBT wallet
  public get subscriptionWalletTransactions(): WalletTransaction[] {
    return this.walletTransactions.filter(
      (transaction: WalletTransaction) =>
        transaction.type === WalletTransactionType.Subscription ||
        transaction.type === WalletTransactionType.FeePerTrade
    );
  }

  /**
   * 👾 Mutation that sets the wallet's deposit address
   * @param depositAddress
   */
  @Mutation
  public SET_DEPOSIT_ADDRESS(depositAddress: string) {
    this.depositWalletAddress = depositAddress;
  }

  /**
   * 👾 Mutation that sets the current user's hot wallet balance
   * @param hotWalletBalance
   */
  @Mutation
  public SET_HOT_WALLET_BALANCE(hotWalletBalance: number) {
    this.hotWalletBalance = hotWalletBalance;
  }

  /**
   * 👾 Mutation that sets the current user's cold wallet balance
   * @param coldWalletBalance
   */
  @Mutation
  public SET_COLD_WALLET_BALANCE(coldWalletBalance: number) {
    this.coldWalletBalance = coldWalletBalance;
  }

  /**
   * 👾 Mutation that sets the current price of OBT in USD
   * @param currentOBTPrice
   */
  @Mutation
  public SET_CURRENT_OBT_PRICE(currentOBTPrice: number) {
    this.currentOBTPrice = currentOBTPrice;
  }

  /**
   * 👾 Mutation that sets the current user's wallet transactions
   * @param walletTransactions
   */
  @Mutation
  public SET_TRANSACTION_HISTORY(walletTransactions: WalletTransaction[]) {
    this.walletTransactions = walletTransactions;
  }

  /**
   * 👾 Mutation that sets the current user's wallet transactions
   * @param walletTransactions
   */
  @Mutation
  public SET_OBT_HOLDING_RANK(holdingRank: string) {
    this.OBTHoldingRank = holdingRank;
  }

  /**
   * 👾 Mutation that sets the estimated withdraw fees
   * @param walletTransactions
   */
  @Mutation
  public SET_ESTIMATED_FEES(estimatedFees: any) {
    this.estimatedFees = estimatedFees;
  }

  /**
   * 👾 Mutation that sets the last time balance was fetched
   * @param lastUpdateTime
   */
  @Mutation
  public SET_LAST_WALLET_BALANCE_UPDATE(lastUpdateTime: Date) {
    this.lastUpdateTime = lastUpdateTime;
  }

  /**
   * 🔨 Changes the loading flag for the app bar
   */
  @Action
  public fetchTransactionEstimatedFee() {
    Vue.prototype.$api
      .get("/api/v1/token/estimatefee")
      .then((response: any) =>
        this.context.commit("SET_ESTIMATED_FEES", camelcaseKeys(response.data))
      );
  }

  /**
   * 🚀 Fetches the current user's OBT balance from the API
   */
  @Action
  public async fetchWalletBalance() {
    try {
      let response = await Vue.prototype.$api.get(
        "/api/v1/token/balance",
        null,
        Emit.None
      );

      let formattedResponse = camelcaseKeys(response, { deep: true }).data;

      this.context.commit(
        "SET_DEPOSIT_ADDRESS",
        formattedResponse.walletAddress
      );
      this.context.commit(
        "SET_HOT_WALLET_BALANCE",
        formattedResponse.balance.active
      );
      this.context.commit(
        "SET_COLD_WALLET_BALANCE",
        formattedResponse.balance.pending
      );
      this.context.commit(
        "SET_CURRENT_OBT_PRICE",
        formattedResponse.obtUsdPrice
      );
      this.context.commit("SET_LAST_WALLET_BALANCE_UPDATE", new Date());
    } catch (error) {}
  }

  /**
   * 🚀 Fetches the available transaction history for the current user's wallet
   */
  @Action
  public fetchWalletTransactionHistory() {
    Vue.prototype.$api
      .get("/api/v1/token/transaction")
      .then((response: any) => {
        this.context.commit("SET_TRANSACTION_HISTORY", response.data.result);
      });
  }

  /**
   * 🚀 Fetches the OBT holding rank for the current user
   */
  @Action
  public fetchUserOBTHoldingRank() {
    Vue.prototype.$api.get("/api/v1/users/getrank").then((response: any) => {
      let rank = response.data.rank;
      this.context.commit("SET_OBT_HOLDING_RANK", rank < 1000 ? rank : "999+");
    });
  }

  /**
   * 🚀 Initiates a withdrawal for the current user on the API
   */
  @Action
  public async requestWithdrawal(withdrawalDetails: any) {
    return Vue.prototype.$api.post("/api/v1/token/requestwithdraw", {
      address: withdrawalDetails.address,
      amount: withdrawalDetails.amount,
      password: withdrawalDetails.password,
      otp_code: withdrawalDetails.otpCode,
    });
  }
}
